import type { TFunction } from "i18next";
import {
  AlertCircle,
  Calendar as CalendarStepIcon,
  Check,
  CreditCard,
  Loader2,
  User,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { PriceDisplay } from "@/components/ui/price-display";
import { cn } from "@/lib/utils";
import { useAppointmentCreation } from "../hooks/use-appointment-creation";
import { useCustomerStep } from "../hooks/use-customer-step";
import { useSchedulingStep } from "../hooks/use-scheduling-step";
import { useServicesStep } from "../hooks/use-services-step";
// Hooks
import type { CreateAppointmentStep } from "../hooks/use-step-navigation";
import { useStepNavigation } from "../hooks/use-step-navigation";
// Step components
import { DesktopCustomerStep } from "./steps/customer-step/desktop-customer-step";
import { DesktopSchedulingStep } from "./steps/scheduling-step/desktop-scheduling-step";
import { DesktopServicesStep } from "./steps/services-step/desktop-services-step";

interface DesktopCreateAppointmentProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  prefilledData?: {
    customerId?: string;
    workerId?: string;
    startTime?: Date;
    date?: string;
    time?: string;
    notes?: string;
  };
}

const getSteps = (
  t: TFunction
): Array<{
  id: CreateAppointmentStep;
  title: string;
  icon: React.ComponentType<{ className?: string }>;
}> => [
  { id: "customer", title: t("booking.steps.customer"), icon: User },
  { id: "services", title: t("booking.steps.services"), icon: CreditCard },
  {
    id: "scheduling",
    title: t("booking.steps.scheduling"),
    icon: CalendarStepIcon,
  },
];

export function DesktopCreateAppointment({
  open,
  onOpenChange,
  onSuccess,
  prefilledData,
}: DesktopCreateAppointmentProps) {
  const { t } = useTranslation("appointments");
  const steps = getSteps(t);

  // Initialize step hooks
  const customerStep = useCustomerStep({
    prefilledCustomerId: prefilledData?.customerId,
    readonly: !!prefilledData?.customerId, // Make readonly when customer is pre-selected
  });

  const servicesStep = useServicesStep();

  const schedulingStep = useSchedulingStep({
    prefilledData,
  });

  const {
    handleCreateAppointment,
    loading: createLoading,
    error: createError,
  } = useAppointmentCreation({
    onSuccess,
    onClose: () => onOpenChange(false),
  });

  // Step navigation - start on services step if customer is pre-selected
  const stepNavigation = useStepNavigation({
    initialStep: prefilledData?.customerId ? "services" : "customer",
    canGoToCustomer: () => true,
    canGoToServices: () => customerStep.isValid(),
    canGoToScheduling: () => customerStep.isValid() && servicesStep.isValid(),
  });

  const {
    currentStep,
    goToStep,
    nextStep,
    prevStep,
    canGoToStep,
    isFirstStep,
    isLastStep,
    isStepCompleted,
  } = stepNavigation;

  // Handle final submission
  const handleSubmit = async () => {
    if (!customerStep.isValid() || !servicesStep.isValid() || !schedulingStep.isValid()) {
      return;
    }

    try {
      const finalCustomerId = await customerStep.getFinalCustomerId();
      const { startTime, endTime } =
        schedulingStep.getStartEndTimes(servicesStep.totalDuration) || {};

      if (!startTime || !endTime) {
        throw new Error(t("createStep.invalidDateTime"));
      }

      await handleCreateAppointment({
        customerId: finalCustomerId,
        selectedServices: servicesStep.selectedServices,
        startTime,
        notes: schedulingStep.notes,
      });
    } catch (error) {
      console.error("Failed to create appointment:", error);
    }
  };

  const canSubmit = () => {
    return customerStep.isValid() && servicesStep.isValid() && schedulingStep.isValid() && !loading;
  };

  const renderCurrentStep = () => {
    // Separate loading states to prevent input focus loss
    const customerStepLoading = createLoading; // Only creation loading affects customer step
    const servicesStepLoading = servicesStep.loading || createLoading;
    const schedulingStepLoading = schedulingStep.loading || createLoading;

    switch (currentStep) {
      case "customer":
        return <DesktopCustomerStep customerStep={customerStep} loading={customerStepLoading} />;
      case "services":
        return <DesktopServicesStep servicesStep={servicesStep} loading={servicesStepLoading} />;
      case "scheduling":
        return (
          <DesktopSchedulingStep schedulingStep={schedulingStep} loading={schedulingStepLoading} />
        );
      default:
        return null;
    }
  };

  const loading =
    customerStep.loading || servicesStep.loading || schedulingStep.loading || createLoading;
  const error = customerStep.error || servicesStep.error || schedulingStep.error || createError;

  // Reset all steps when dialog closes
  if (!open) {
    return null;
  }

  return (
    <div className="flex flex-1 rounded-xl overflow-hidden min-h-0">
      {/* Left Sidebar - Step Navigation */}
      <div className="w-80 border-r p-6 bg-card">
        <div className="space-y-4">
          <div>
            <h2 className="text-xl font-semibold">{t("booking.dialog.title")}</h2>
            <p className="text-sm text-muted-foreground mt-1">{t("booking.dialog.subtitle")}</p>
          </div>

          {/* Step Navigation */}
          <div className="space-y-2">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isActive = currentStep === step.id;
              const isCompleted = isStepCompleted(step.id);
              const canClick = canGoToStep(step.id);

              return (
                <button
                  type="button"
                  key={step.id}
                  onClick={() => canClick && goToStep(step.id)}
                  disabled={!canClick}
                  data-test-id={`appointment-create-step-${step.id}`}
                  className={cn(
                    "w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors",
                    isActive
                      ? "bg-primary text-primary-foreground border"
                      : isCompleted
                        ? "bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800"
                        : canClick
                          ? "hover:bg-accent text-accent-foreground"
                          : "text-muted-foreground cursor-not-allowed"
                  )}
                >
                  <div
                    className={cn(
                      "flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center",
                      isActive
                        ? "bg-primary-foreground text-primary"
                        : isCompleted
                          ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300"
                          : "bg-muted text-muted-foreground"
                    )}
                  >
                    {isCompleted ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
                  </div>
                  <div>
                    <div className="font-medium">{step.title}</div>
                    <div className="text-xs">
                      {t("booking.dialog.stepProgress", {
                        current: index + 1,
                        total: steps.length,
                      })}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Summary */}
          {(customerStep.customerInput || servicesStep.selectedServices.length > 0) && (
            <div className="border-t pt-4 mt-6">
              <h3 className="font-medium mb-2">{t("booking.dialog.summary")}</h3>
              <div className="text-sm text-muted-foreground space-y-1">
                {customerStep.customerInput && (
                  <div>
                    {t("booking.dialog.summaryCustomer", {
                      name: customerStep.customerInput,
                    })}
                  </div>
                )}
                {servicesStep.selectedServices.length > 0 && (
                  <div>
                    {t("booking.dialog.summaryServices", {
                      count: servicesStep.selectedServices.length,
                    })}
                  </div>
                )}
                {servicesStep.totalPrice > 0 && (
                  <div className="flex items-center gap-1">
                    {t("booking.dialog.summaryTotalLabel")}{" "}
                    <PriceDisplay amount={servicesStep.totalPrice} />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Right Content Area */}
      <div className="flex-1 flex flex-col min-h-0">
        {error && (
          <Alert variant="destructive" className="m-6 mb-0">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error.message}</AlertDescription>
          </Alert>
        )}

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto p-6">{renderCurrentStep()}</div>

        {/* Footer Actions */}
        <div className="border-t bg-card p-6 flex justify-between">
          <Button
            variant="outline"
            onClick={prevStep}
            disabled={isFirstStep()}
            data-test-id="appointment-create-back-button"
          >
            {t("actions.back")}
          </Button>

          <div className="flex gap-2">
            {!isLastStep() ? (
              <Button
                onClick={nextStep}
                disabled={
                  (currentStep === "customer" && !canGoToStep("services")) ||
                  (currentStep === "services" && !canGoToStep("scheduling"))
                }
                data-test-id="appointment-create-next-button"
              >
                {t("actions.next")}
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={!canSubmit()}
                data-test-id="appointment-create-submit-button"
              >
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t("createAppointment")}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
